/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The shopping engine: one per tab, shared by every shopping screen.
 *
 *   load      GET /api/shopping/state — every list and live item at once
 *   follow    the household stream; on a drop, reconnect from the last
 *             position with backoff, so nothing is missed or repeated
 *   write     operations go to an outbox (kept in localStorage, so closing
 *             the tab mid-aisle loses nothing), shown at once, sent in
 *             batches, and dropped from the outbox only once the feed has
 *             caught up past them — no flicker between "sent" and "seen"
 *
 * The screens read one immutable snapshot through useSyncExternalStore and
 * never talk to the network themselves.
 */

import {
  ShoppingLiveApi, followStream, type LiveChange, type OpResult, type ShoppingOp,
} from "../../../api/shoppingLive.ts";
import { ApiError } from "../../../api/client.ts";
import { applyChange, applyOp, fromState, type Model } from "./model.ts";

export type LiveStatus = "loading" | "live" | "connecting" | "offline" | "error";

interface Pending {
  op: ShoppingOp;
  /** Set once the server has answered: drop when the feed reaches this. */
  settleSeq?: number;
  sending?: boolean;
}

export interface Snapshot {
  model: Model | null;
  status: LiveStatus;
  error: string | null;
  /** Operations not yet confirmed by the server. */
  unsent: number;
}

const OUTBOX_KEY = "mp.shopping.outbox.v1";
const BACKOFF_MS = [1000, 2000, 5000, 10_000, 30_000];
const IDLE_STOP_MS = 15_000;

function loadOutbox(): Pending[] {
  try {
    const raw = localStorage.getItem(OUTBOX_KEY);
    const ops = raw ? (JSON.parse(raw) as ShoppingOp[]) : [];
    return Array.isArray(ops) ? ops.map((op) => ({ op })) : [];
  } catch {
    return [];
  }
}

export class ShoppingEngine {
  private confirmed: Model | null = null;
  private pending: Pending[] = loadOutbox();
  private snapshot: Snapshot = { model: null, status: "loading", error: null, unsent: 0 };
  private listeners = new Set<() => void>();
  private abort: AbortController | null = null;
  private running = false;
  /** Bumped on every start, so a loop left over from before a stop exits. */
  private generation = 0;
  private flushing = false;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private streamLive = false;
  private onRejected: ((message: string) => void) | null = null;

  // ── React plumbing ────────────────────────────────────────────────────────

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
    if (!this.running) this.start();
    return () => {
      this.listeners.delete(fn);
      if (!this.listeners.size) {
        // Navigating from the lists screen to a list unmounts one and mounts
        // the other; don't drop the stream for that.
        this.idleTimer = setTimeout(() => { if (!this.listeners.size) this.stop(); }, IDLE_STOP_MS);
      }
    };
  };

  getSnapshot = (): Snapshot => this.snapshot;

  setRejectionHandler(fn: ((message: string) => void) | null): void {
    this.onRejected = fn;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  private start(): void {
    this.running = true;
    window.addEventListener("online", this.onOnline);
    void this.loop(++this.generation);
  }

  private stop(): void {
    this.running = false;
    this.abort?.abort();
    this.abort = null;
    window.removeEventListener("online", this.onOnline);
    // Forget the household's data when nobody is looking, so a later sign-in
    // as someone else can never be shown it. The outbox stays: it is ours.
    this.confirmed = null;
    this.publish({ status: "loading", model: null });
  }

  private onOnline = (): void => { this.abort?.abort(); void this.flush(); };

  private async loop(gen: number): Promise<void> {
    let attempt = 0;
    const current = () => this.running && gen === this.generation;
    while (current()) {
      try {
        if (!this.confirmed) {
          this.publish({ status: this.snapshot.model ? "connecting" : "loading" });
          const state = await ShoppingLiveApi.state();
          if (!current()) return;
          this.confirmed = fromState(state);
          this.recompute();
          void this.flush();
        }
        this.abort = new AbortController();
        await followStream(this.confirmed.seq, {
          onReady: () => {
            attempt = 0;
            this.streamLive = true;
            this.publish({ status: "live", error: null });
          },
          onChange: (c) => this.receive([c]),
        }, this.abort.signal);
      } catch (err) {
        if (!current()) return;
        if (err instanceof ApiError && err.isAuth) {
          // Keep trying: the token is fetched fresh on every attempt.
          this.publish({ status: "error", error: "Sign in again to see the shopping lists." });
        } else if (!(err instanceof DOMException && err.name === "AbortError") && !this.confirmed) {
          this.publish({ status: navigator.onLine ? "error" : "offline",
            error: (err as Error).message });
        }
      }
      this.streamLive = false;
      if (!current()) return;
      if (this.confirmed) this.publish({ status: navigator.onLine ? "connecting" : "offline" });
      const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]!;
      attempt++;
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  // ── Reading ───────────────────────────────────────────────────────────────

  private receive(changes: LiveChange[]): void {
    if (!this.confirmed) return;
    let m = this.confirmed;
    for (const c of changes) m = applyChange(m, c);
    this.confirmed = m;
    const before = this.pending.length;
    this.pending = this.pending.filter((p) => p.settleSeq === undefined || p.settleSeq > m.seq);
    if (this.pending.length !== before) this.saveOutbox();
    this.recompute();
  }

  /** When the stream is down, the feed is read directly after each write. */
  private async catchUp(): Promise<void> {
    if (!this.confirmed) return;
    for (let i = 0; i < 10; i++) {
      const page = await ShoppingLiveApi.changes(this.confirmed.seq);
      this.receive(page.changes);
      if (!page.more) {
        // Nothing new for an op that changed nothing: settle it anyway.
        if (this.confirmed.seq < page.latestSeq) this.confirmed = { ...this.confirmed, seq: page.latestSeq };
        this.receive([]);
        return;
      }
    }
  }

  // ── Writing ───────────────────────────────────────────────────────────────

  submit(ops: Array<Omit<ShoppingOp, "id" | "clientTs"> & { id?: string }>): ShoppingOp[] {
    const now = new Date().toISOString();
    const full = ops.map((o) => ({ ...o, id: o.id ?? crypto.randomUUID(), clientTs: now }) as ShoppingOp);
    this.pending.push(...full.map((op) => ({ op })));
    this.saveOutbox();
    this.recompute();
    void this.flush();
    return full;
  }

  private async flush(): Promise<void> {
    if (this.flushing || !this.confirmed) return;
    const batch = this.pending.filter((p) => p.settleSeq === undefined && !p.sending).slice(0, 50);
    if (!batch.length) return;
    this.flushing = true;
    for (const p of batch) p.sending = true;
    try {
      const r = await ShoppingLiveApi.ops(batch.map((p) => p.op));
      const byId = new Map<string, OpResult>(r.results.map((x) => [x.id, x]));
      const rejected: string[] = [];
      for (const p of batch) {
        p.sending = false;
        const res = byId.get(p.op.id);
        if (!res) continue;
        if (res.status === "rejected") {
          rejected.push(res.error ?? "That change couldn't be saved.");
          p.settleSeq = -1;
        } else {
          p.settleSeq = r.latestSeq;
        }
      }
      this.pending = this.pending.filter((p) => p.settleSeq !== -1);
      if (rejected.length) this.onRejected?.(rejected[0]!);
      this.saveOutbox();
      if (!this.streamLive) await this.catchUp();
      else this.receive([]);
    } catch (err) {
      for (const p of batch) p.sending = false;
      if (err instanceof ApiError && err.status >= 400 && err.status < 500 && !err.isAuth) {
        // The batch itself was refused (malformed): don't retry it forever.
        this.pending = this.pending.filter((p) => !batch.includes(p));
        this.saveOutbox();
        this.recompute();
        this.onRejected?.(err.message);
      } else {
        this.scheduleFlush(5000);
      }
    } finally {
      this.flushing = false;
    }
    if (this.pending.some((p) => p.settleSeq === undefined && !p.sending)) void this.flush();
  }

  private scheduleFlush(ms: number): void {
    if (this.flushTimer) return;
    this.flushTimer = setTimeout(() => { this.flushTimer = null; void this.flush(); }, ms);
  }

  private saveOutbox(): void {
    try {
      const unsent = this.pending.filter((p) => p.settleSeq === undefined).map((p) => p.op);
      localStorage.setItem(OUTBOX_KEY, JSON.stringify(unsent));
    } catch {
      // Private mode or a full quota: the outbox still works while the tab is open.
    }
  }

  // ── The view ──────────────────────────────────────────────────────────────

  private recompute(): void {
    if (!this.confirmed) { this.publish({ model: null }); return; }
    const now = new Date().toISOString();
    let m = this.confirmed;
    for (const p of this.pending) m = applyOp(m, p.op, now);
    this.publish({ model: m });
  }

  private publish(patch: Partial<Snapshot>): void {
    this.snapshot = {
      ...this.snapshot,
      ...patch,
      unsent: this.pending.filter((p) => p.settleSeq === undefined).length,
    };
    for (const fn of this.listeners) fn();
  }
}

export const shoppingEngine = new ShoppingEngine();
